"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import React from "react"

import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import {
  BookOpen,
  FileUp,
  X,
  Loader,
  Book,
  Calendar,
  BookText,
  Award,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
} from "lucide-react"
import { Worker, Viewer } from "@react-pdf-viewer/core"
import "@react-pdf-viewer/core/lib/styles/index.css"
import { generateQuestionsWithAI, getAllbooks } from "./api"
import { postTeacherAssignmentFile, generatePresignedUrl } from "../postFileAws"

const AssignmentCreationForm = () => {
  const navigate = useNavigate()
  const [title, setTitle] = useState("")
  const [dueDate, setDueDate] = useState("")
  const topicRef = useRef("")
  const aiPromptRef = useRef("")
  const [topic, setTopic] = useState("")
  const [aiPrompt, setAiPrompt] = useState("")
  const titleRef = useRef("")

  const [questionCounts, setQuestionCounts] = useState({
    essay: 1,
    shortAnswer: 1,
    MCQ: 1,
  })
  const [selectedDifficulties, setSelectedDifficulties] = useState(["medium"])
  const [pdfFile, setPdfFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLibraryLoading, setIsLibraryLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [isAIMode, setIsAIMode] = useState(true)
  const [refUri, setRefUri] = useState("")
  const selectedClass = useSelector((state) => state.teacherSub.selectedClass)
  const selectedSubject = useSelector((state) => state.teacherSub.selectedSubject)
  const teacherId = useSelector((state) => state.teacherSub.teacherId)
  const classCode = useSelector((state) => state.teacherSub.classCode)
  const organisationId = localStorage.getItem("organisationId")
  const [refType, setRefType] = useState("")
  const [refMaterialSource, setRefMaterialSource] = useState("upload")
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false)
  const [books, setBooks] = useState([])
  const [selectedBook, setSelectedBook] = useState(null)
  const [previewBook, setPreviewBook] = useState(null)
  const [totalQuestionCount, setTotalQuestionCount] = useState(0)
  const [marksPerQuestion, setMarksPerQuestion] = useState({
    essay: 5,
    shortAnswer: 3,
    MCQ: 1,
  })
  // Mobile collapsible sections state
  const [isMobile, setIsMobile] = useState(false)
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    aiPrompt: false,
    refMaterial: false,
    difficulty: false,
    questionTypes: false,
  })

  const classRoomId = localStorage.getItem("classRoomId")
  const expandedSectionsRef = useRef({
    basicInfo: true,
    aiPrompt: false,
    refMaterial: false,
    difficulty: false,
    questionTypes: false,
  })

  useEffect(() => {
    const oneWeekFromNow = new Date()
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7)
    setDueDate(oneWeekFromNow.toISOString().split("T")[0])
  }, [])

  useEffect(() => {
    const total = questionCounts.essay + questionCounts.shortAnswer + questionCounts.MCQ
    setTotalQuestionCount(total)
  }, [questionCounts])

  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIfMobile()
    window.addEventListener("resize", checkIfMobile)

    return () => {
      window.removeEventListener("resize", checkIfMobile)
    }
  }, [])

  useEffect(() => {
    const selectedBookUri = localStorage.getItem("selectedBookUri")
    if (selectedBookUri) {
      setRefUri(selectedBookUri)
      setRefType("pdf")
      setRefMaterialSource("library")

      // Fetch the book details based on the URI
      const fetchSelectedBook = async () => {
        try {
          const response = await getAllbooks(classRoomId)
          if (response.body.data) {
            const selectedBook = response.body.data.find((book) => book.fileUri === selectedBookUri)
            if (selectedBook) {
              setSelectedBook(selectedBook)
            }
          }
        } catch (error) {
          console.error("Error fetching selected book:", error)
        }
      }

      fetchSelectedBook()
      localStorage.removeItem("selectedBookUri") // Clear the stored URI after using it
    }
  }, [classRoomId])

  const fetchBooks = useCallback(async () => {
    if (!classRoomId) {
      setIsLibraryLoading(false)
      return
    }

    try {
      setIsLibraryLoading(true)
      const response = await getAllbooks(classRoomId)
      if (response.body.data) {
        const booksWithPresignedUrls = await Promise.all(
          response.body.data.map(async (book) => {
            const presignedUrl = await generatePresignedUrl(book.fileUrl)
            return { ...book, presignedUrl }
          }),
        )
        setBooks(booksWithPresignedUrls)
      } else {
        setBooks([])
      }
    } catch (error) {
      console.error("Error fetching books:", error)
      setError("Failed to load books. Please try again.")
    } finally {
      setIsLibraryLoading(false)
    }
  }, [classRoomId])

  const questionTypes = [
    { id: "essay", label: "Essay Type", icon: BookOpen, color: "bg-purple-100", iconColor: "text-purple-600" },
    { id: "shortAnswer", label: "Short Answer", icon: BookText, color: "bg-blue-100", iconColor: "text-blue-600" },
    { id: "MCQ", label: "MCQ", icon: CheckCircle, color: "bg-green-100", iconColor: "text-green-600" },
  ]

  const difficultyLevels = [
    { id: "easy", label: "Easy", color: "bg-green-600", hoverColor: "hover:bg-green-700" },
    { id: "medium", label: "Medium", color: "bg-blue-600", hoverColor: "hover:bg-blue-700" },
    { id: "hard", label: "Hard", color: "bg-red-600", hoverColor: "hover:bg-red-700" },
  ]
  const toggleSection = (section) => {
    expandedSectionsRef.current[section] = !expandedSectionsRef.current[section]
    setForceRender((prev) => !prev) // This forces a minimal re-render only when needed
  }

  const [forceRender, setForceRender] = useState(false) // Add this state

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 7 * 1024 * 1024) {
        setError("Max limit reached, please import this file from library")
        setPdfFile(null)
        return
      }
      if (file.type === "application/pdf" || file.type.startsWith("image/")) {
        setPdfFile(file)
        console.log("ye sb jayga",file,"title",title);
        
        const response = await postTeacherAssignmentFile(file, teacherId, title, "refMaterial")
        console.log(response)
        setRefUri(response.uri)
        setRefType(file.type === "application/pdf" ? "pdf" : "image")
        setError("")
      } else {
        setError("Please upload a PDF or image file")
        setPdfFile(null)
      }
    }
  }

  const handleLibrarySelection = async () => {
    try {
      setIsLibraryModalOpen(true)
      await fetchBooks()
      if (refUri) {
        const selectedBook = books.find((book) => book.fileUri === refUri)
        if (selectedBook) {
          setSelectedBook(selectedBook)
          setPreviewBook({ ...selectedBook, presignedUrl: selectedBook.presignedUrl || selectedBook.fileUrl })
        }
      }
    } catch (error) {
      console.error("Error fetching books:", error)
      setError("Failed to load books from the library")
    }
  }

  const handleBookSelect = (book) => {
    setSelectedBook(book)
    setPreviewBook({ ...book, presignedUrl: book.presignedUrl || book.fileUrl })
  }

  const handleConfirmBook = () => {
    if (selectedBook) {
      setRefUri(selectedBook.fileUri)
      setRefType("pdf")
      setPdfFile(null)
      setIsLibraryModalOpen(false)
    }
  }

  const saveAssignmentData = () => {
    const assignmentData = {
      assignment_topic: topic,
      assignmentTitle: title,
      assignmentDueDate: dueDate,
      class: selectedClass,
      subject: selectedSubject,
      teacherId: teacherId,
      organisationId:organisationId,
      ...(topic && { topic }),
      mcqNumber: questionCounts.MCQ,
      shortAnswerNumber: questionCounts.shortAnswer,
      essayNumber: questionCounts.essay,
      questionDifficulty: selectedDifficulties,
      referenceType: refMaterialSource === "upload" ? refType : "library",
      refMaterial: refUri,
      userPrompt: aiPrompt,
      classCode: classCode,
      classRoomId: classRoomId,
    }

    localStorage.setItem("assignmentData", JSON.stringify(assignmentData))
    localStorage.setItem("allAttachment", JSON.stringify(refMaterialSource === "upload" ? [pdfFile] : []))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (totalQuestionCount === 0) {
      setError("At least one question is required.")
      return
    }
    setIsLoading(true)
    setError("")
    setSuccess("")

    try {
      const marksInfo = `MCQ questions must be of ${marksPerQuestion.MCQ} marks each, Short Answer questions must be of ${marksPerQuestion.shortAnswer} marks each, and Essay questions must be of ${marksPerQuestion.essay} marks each.`
      const updatedAiPrompt = aiPrompt ? `${aiPrompt}\n\n${marksInfo}` : marksInfo

      const assignmentData = {
        assignment_topic: topic,
        assignmentTitle: title,
        assignmentDescription: "",
        assignmentDueDate: dueDate,
        teacherId: teacherId,
        organisationId:organisationId,
        classCode: classCode,
        classRoomId: classRoomId,
        topic,
        questionNumber: questionCounts.MCQ + questionCounts.shortAnswer + questionCounts.essay,
        mcqNumber: questionCounts.MCQ,
        shortAnswerNumber: questionCounts.shortAnswer,
        essayNumber: questionCounts.essay,
        questionDifficulty: selectedDifficulties,
        questionType: ["shortAnswerNumber", "essayNumber", "MCQ"],
        referenceType: refType || "",
        refMaterial: refUri || "",
        classValue: selectedClass,
        userPrompt: updatedAiPrompt,
      }

      console.log("Scheduling assignment creation")
      const response = await generateQuestionsWithAI(assignmentData)
      console.log("Server response:", response)

      if (response && response.success === true) {
        console.log("Assignment creation scheduled successfully")
        setSuccess("Assignment is being created!")
        setTimeout(() => navigate("/assignments"), 2000)
      } else {
        throw new Error(response?.error || "Failed to create assignment")
      }
    } catch (err) {
      console.error("Error in handleSubmit:", err)
      setError(err.message || "Failed to create assignment")
    } finally {
      setIsLoading(false)
    }
  }

  const handleManualCreation = () => {
    if (!title.trim()) {
      alert("Assignment title is compulsory")
      return
    }
    saveAssignmentData()
    navigate("/assignments/createWithAI/createManually")
  }

  // Mobile collapsible section component
  const MobileCollapsibleSection = React.memo(({ title, id, icon, children }) => {
    const isExpanded = expandedSectionsRef.current[id]
    const Icon = icon

    return (
      <div className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden transition-all duration-300 hover:shadow-md">
        <button
          type="button"
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between p-4 text-left bg-gradient-to-r from-indigo-50 to-purple-50"
        >
          <div className="flex items-center">
            {icon && <Icon className="w-5 h-5 text-indigo-600 mr-3" />}
            <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-600" />
          )}
        </button>
        <div
          className={`transition-all duration-300 ${isExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}`}
        >
          <div className="p-4">{children}</div>
        </div>
      </div>
    )
  })

  // Render mobile or desktop view based on screen size
  const renderFormContent = () => {
    if (isMobile) {
      return (
        <div className="space-y-4">
          <div className="mb-4">
            <a
              href="/assignments"
              className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="text-sm font-medium">Back to Assignments</span>
            </a>
          </div>
          <MobileCollapsibleSection title="Basic Information" id="basicInfo" icon={BookOpen}>
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Assignment Title
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="title"
                    ref={(el) => {
                      if (el) el.value = title
                    }}
                    onChange={(e) => (titleRef.current = e.target.value)}
                    onBlur={() => setTitle(titleRef.current)}
                    required
                    placeholder="Enter title"
                    className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BookOpen className="h-5 w-5 text-indigo-500" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
                <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                  Topic
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="topic"
                    ref={(el) => {
                      if (el) el.value = topic
                    }}
                    onChange={(e) => (topicRef.current = e.target.value)}
                    onBlur={() => setTopic(topicRef.current)}
                    required
                    placeholder="Enter topic"
                    className="w-full pl-8 pr-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BookText className="h-5 w-5 text-indigo-500" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="dueDate"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-indigo-500" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">Class-Subject</label>
                <div className="relative">
                  <input
                    type="text"
                    value={`${selectedClass}-${selectedSubject}`}
                    readOnly
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                    placeholder="Class-Subject"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Book className="h-5 w-5 text-indigo-500" />
                  </div>
                </div>
              </div>

              {isAIMode && (
                <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
                  <label htmlFor="aiPrompt" className="block text-sm font-medium text-gray-700 mb-2">
                    AI Prompt (Optional)
                  </label>
                  <textarea
                    id="aiPrompt"
                    ref={(el) => {
                      if (el) el.value = aiPrompt
                    }}
                    onChange={(e) => (aiPromptRef.current = e.target.value)}
                    onBlur={() => setAiPrompt(aiPromptRef.current)}
                    placeholder={`Create an assignment title ${title} on the topic ${topic}. Please adhere to the reference material provided. Special instructions are as follows:`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 h-24 resize-none"
                  />
                </div>
              )}
            </div>
          </MobileCollapsibleSection>

          {isAIMode && (
            <>
              <MobileCollapsibleSection title="Reference Material" id="refMaterial" icon={FileUp}>
                <div className="flex items-center justify-between mb-4">
                  <ToggleSwitch
                    isOn={refMaterialSource === "library"}
                    handleToggle={() => setRefMaterialSource(refMaterialSource === "upload" ? "library" : "upload")}
                    onLabel="Library"
                    offLabel="Upload"
                  />
                </div>

                {refMaterialSource === "upload" ? (
                  <div className="mt-3">
                    <label htmlFor="fileInput" className="cursor-pointer block">
                      <div className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-indigo-300 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors duration-200">
                        <FileUp className="w-8 h-8 text-indigo-500 mb-2" />
                        <p className="text-sm text-indigo-600 font-medium">Click to upload PDF or Image</p>
                        <p className="text-xs text-gray-500 mt-1">Max size: 7MB</p>
                      </div>
                    </label>
                    <input
                      id="fileInput"
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      accept=".pdf,image/*"
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleLibrarySelection}
                    className="w-full mt-3 px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                  >
                    <Book className="w-4 h-4 mr-2 inline-block" />
                    Select from Library
                  </button>
                )}

                {(pdfFile || (refMaterialSource === "library" && (selectedBook || refUri))) && (
                  <div className="mt-4 flex items-center justify-between bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                    <span className="text-sm text-indigo-700 truncate max-w-[80%]">
                      {pdfFile ? pdfFile.name : selectedBook ? selectedBook.fileTitle : refUri ? "Selected book" : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setPdfFile(null)
                        setRefUri("")
                        setSelectedBook(null)
                      }}
                      className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </MobileCollapsibleSection>

              <MobileCollapsibleSection title="Difficulty Level" id="difficulty" icon={Award}>
                <div className="grid grid-cols-1 gap-3">
                  {difficultyLevels.map((level) => (
                    <button
                      key={level.id}
                      type="button"
                      onClick={() => setSelectedDifficulties([level.id])}
                      className={`flex items-center justify-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedDifficulties.includes(level.id)
                          ? `${level.color} text-white shadow-md transform scale-[1.02]`
                          : `bg-gray-100 text-gray-700 hover:bg-gray-200`
                      }`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </MobileCollapsibleSection>

              <MobileCollapsibleSection title="Question Types" id="questionTypes" icon={BookOpen}>
                <div className="space-y-2.5">
                  {questionTypes.map((type) => (
                    <div
                      key={type.id}
                      className={`${type.color} rounded-lg p-3 transition-all duration-200 hover:shadow-md`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium flex items-center gap-2">
                          <type.icon className={`w-4 h-4 ${type.iconColor}`} />
                          {type.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col">
                          <label className="text-xs text-gray-600 mb-0.5">Question Count</label>
                          <input
                            type="number"
                            min="0"
                            value={questionCounts[type.id]}
                            onChange={(e) => {
                              const value = Math.max(0, Number.parseInt(e.target.value) || 0)
                              setQuestionCounts((prev) => ({
                                ...prev,
                                [type.id]: value,
                              }))
                            }}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Count"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label className="text-xs text-gray-600 mb-0.5">Marks per Question</label>
                          <input
                            type="number"
                            min="1"
                            value={marksPerQuestion[type.id]}
                            onChange={(e) => {
                              const value = Math.max(1, Number.parseInt(e.target.value) || 1)
                              setMarksPerQuestion((prev) => ({
                                ...prev,
                                [type.id]: value,
                              }))
                            }}
                            placeholder="Marks"
                            className={`w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                              questionCounts[type.id] > 0 ? "" : "opacity-50 cursor-not-allowed"
                            }`}
                            disabled={questionCounts[type.id] === 0}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </MobileCollapsibleSection>
            </>
          )}
        </div>
      )
    } else {
      // Desktop view - exactly as in the original code
      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Assignment Title
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder="Enter title"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <BookOpen className="h-5 w-5 text-indigo-500" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="dueDate"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-indigo-500" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
              <label className="block text-sm font-medium text-gray-700 mb-1">Class-Subject</label>
              <div className="relative">
                <input
                  type="text"
                  value={`${selectedClass}-${selectedSubject}`}
                  readOnly
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  placeholder="Class-Subject"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Book className="h-5 w-5 text-indigo-500" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
              <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                Topic
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  required
                  placeholder="Enter topic"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <BookText className="h-5 w-5 text-indigo-500" />
                </div>
              </div>
            </div>
          </div>

          {isAIMode && (
            <>
              <div className="bg-white rounded-xl shadow-sm p-4 transition-all duration-300 hover:shadow-md">
                <label htmlFor="aiPrompt" className="block text-sm font-medium text-gray-700 mb-2">
                  AI Prompt (Optional)
                </label>
                <textarea
                  id="aiPrompt"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={`Create an assignment title ${title} on the topic ${topic}. Please adhere to the the reference material provided. Special instructions are as follows : `}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 h-32 resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl shadow-md p-5 transition-all duration-300 hover:shadow-lg">
                  <div className="flex items-center mb-4">
                    <div className="p-2 rounded-full bg-indigo-100 mr-3">
                      <FileUp className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Reference Material</h3>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <ToggleSwitch
                      isOn={refMaterialSource === "library"}
                      handleToggle={() => setRefMaterialSource(refMaterialSource === "upload" ? "library" : "upload")}
                      onLabel="Library"
                      offLabel="Upload"
                    />
                  </div>

                  {refMaterialSource === "upload" ? (
                    <div className="mt-3">
                      <label htmlFor="fileInput" className="cursor-pointer block">
                        <div className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-indigo-300 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors duration-200">
                          <FileUp className="w-8 h-8 text-indigo-500 mb-2" />
                          <p className="text-sm text-indigo-600 font-medium">Click to upload PDF or Image</p>
                          <p className="text-xs text-gray-500 mt-1">Max size: 7MB</p>
                        </div>
                      </label>
                      <input
                        id="fileInput"
                        type="file"
                        className="hidden"
                        onChange={handleFileChange}
                        accept=".pdf,image/*"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleLibrarySelection}
                      className="w-full mt-3 px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                      <Book className="w-4 h-4 mr-2 inline-block" />
                      Select from Library
                    </button>
                  )}

                  {(pdfFile || (refMaterialSource === "library" && (selectedBook || refUri))) && (
                    <div className="mt-4 flex items-center justify-between bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                      <span className="text-sm text-indigo-700 truncate max-w-[80%]">
                        {pdfFile ? pdfFile.name : selectedBook ? selectedBook.fileTitle : refUri ? "Selected book" : ""}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setPdfFile(null)
                          setRefUri("")
                          setSelectedBook(null)
                        }}
                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors duration-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-md p-5 transition-all duration-300 hover:shadow-lg">
                  <div className="flex items-center mb-4">
                    <div className="p-2 rounded-full bg-indigo-100 mr-3">
                      <Award className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Difficulty Level</h3>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    {difficultyLevels.map((level) => (
                      <button
                        key={level.id}
                        type="button"
                        onClick={() => setSelectedDifficulties([level.id])}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          selectedDifficulties.includes(level.id)
                            ? `${level.color} text-white shadow-md transform scale-[1.02]`
                            : `bg-gray-100 text-gray-700 hover:bg-gray-200`
                        }`}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-5 transition-all duration-300 hover:shadow-lg">
                  <div className="flex items-center mb-4">
                    <div className="p-2 rounded-full bg-indigo-100 mr-3">
                      <BookOpen className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Question Types</h3>
                  </div>

                  <div className="space-y-2.5">
                    {questionTypes.map((type) => (
                      <div
                        key={type.id}
                        className={`${type.color} rounded-lg p-3 transition-all duration-200 hover:shadow-md`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium flex items-center gap-2">
                            <type.icon className={`w-4 h-4 ${type.iconColor}`} />
                            {type.label}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex flex-col">
                            <label className="text-xs text-gray-600 mb-0.5">Question Count</label>
                            <input
                              type="number"
                              min="0"
                              value={questionCounts[type.id]}
                              onChange={(e) => {
                                const value = Math.max(0, Number.parseInt(e.target.value) || 0)
                                setQuestionCounts((prev) => ({
                                  ...prev,
                                  [type.id]: value,
                                }))
                              }}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="Count"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-xs text-gray-600 mb-0.5">Marks per Question</label>
                            <input
                              type="number"
                              min="1"
                              value={marksPerQuestion[type.id]}
                              onChange={(e) => {
                                const value = Math.max(1, Number.parseInt(e.target.value) || 1)
                                setMarksPerQuestion((prev) => ({
                                  ...prev,
                                  [type.id]: value,
                                }))
                              }}
                              placeholder="Marks"
                              className={`w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                                questionCounts[type.id] > 0 ? "" : "opacity-50 cursor-not-allowed"
                              }`}
                              disabled={questionCounts[type.id] === 0}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-4 md:p-6 lg:p-8 -mt-5">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 transform hover:shadow-2xl ">
        <div className="p-4 md:p-6 lg:p-8">
          <div className="flex items-center mb-8 relative">
            {!isMobile && (
              <a
                href="/assignments"
                className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors absolute left-0"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">Back to Assignments</span>
              </a>
            )}
            <div className="bg-white rounded-full shadow-md p-1 inline-flex mx-auto">
              <ToggleSwitch
                isOn={isAIMode}
                handleToggle={() => setIsAIMode(!isAIMode)}
                onLabel="Create with AI"
                offLabel="Add Manually"
              />
            </div>
          </div>

          <form onSubmit={isAIMode ? handleSubmit : handleManualCreation} className="space-y-6">
            {renderFormContent()}

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
                <div className="flex">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <div>
                    <p className="text-green-700 font-medium">Success!</p>
                    <p className="text-green-600 text-sm">{success}</p>
                    <p className="text-green-600 text-sm mt-1">Redirecting to assignments page...</p>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                className={`w-full py-3 px-6 rounded-lg text-base font-medium transition-all duration-200 transform hover:scale-[1.01] bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <Loader className="animate-spin mr-2" />
                    Creating Assignment...
                  </div>
                ) : (
                  "Create Assignment"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Library Modal */}
      {isLibraryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-fadeIn">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Select a Book from Library</h3>
              <button
                onClick={() => setIsLibraryModalOpen(false)}
                className="text-white hover:bg-white/20 p-1 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex flex-col md:flex-row h-[70vh]">
              <div className="w-full md:w-1/3 overflow-y-auto border-r border-gray-200 p-2">
                <div className="sticky top-0 bg-white p-2 border-b border-gray-200 mb-2">
                  <h4 className="font-medium text-gray-700">Available Books</h4>
                </div>
                <div className="space-y-2">
                  {books.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No books available</div>
                  ) : (
                    books.map((book) => (
                      <div
                        key={book.fileUrl}
                        className={`p-3 cursor-pointer rounded-lg transition-all duration-200 ${
                          selectedBook === book
                            ? "bg-indigo-100 border-l-4 border-indigo-500"
                            : "hover:bg-gray-100 border-l-4 border-transparent"
                        }`}
                        onClick={() => handleBookSelect(book)}
                      >
                        <div className="flex items-center">
                          <div className="mr-3 bg-indigo-100 p-2 rounded-lg">
                            <Book className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{book.fileTitle}</p>
                            <p className="text-xs text-gray-500">{book.authorName || "Unknown author"}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="w-full md:w-2/3 p-4 overflow-hidden flex flex-col">
                {previewBook ? (
                  <>
                    <div className="bg-indigo-50 p-3 rounded-lg mb-4 border border-indigo-100">
                      <h4 className="text-lg font-semibold text-indigo-700">{previewBook.fileTitle}</h4>
                      {previewBook.authorName && (
                        <p className="text-sm text-indigo-600">By: {previewBook.authorName}</p>
                      )}
                    </div>
                    <div className="flex-grow overflow-hidden rounded-lg border border-gray-200">
                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                        <Viewer fileUrl={previewBook.presignedUrl || previewBook.fileUrl} />
                      </Worker>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300"></div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setIsLibraryModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBook}
                disabled={!selectedBook}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  selectedBook
                    ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Confirm Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLibraryLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl flex flex-col items-center">
            <Loader className="animate-spin text-indigo-600 mb-4" size={48} />
            <p className="text-lg font-semibold text-gray-800">Loading library...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we fetch your books</p>
          </div>
        </div>
      )}
    </div>
  )
}

const ToggleSwitch = ({ isOn, handleToggle, onLabel = "On", offLabel = "Off" }) => {
  return (
    <div className="flex items-center space-x-2">
      <span
        className={`text-sm font-medium transition-colors duration-200 ${!isOn ? "text-indigo-600" : "text-gray-500"}`}
      >
        {offLabel}
      </span>
      <button
        type="button"
        onClick={handleToggle}
        className={`
          relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
          ${isOn ? "bg-indigo-600" : "bg-gray-300"}
        `}
        aria-pressed={isOn}
      >
        <span className="sr-only">{isOn ? onLabel : offLabel}</span>
        <span
          className={`
            inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300
            ${isOn ? "translate-x-6" : "translate-x-1"}
          `}
        />
      </button>
      <span
        className={`text-sm font-medium transition-colors duration-200 ${isOn ? "text-indigo-600" : "text-gray-500"}`}
      >
        {onLabel}
      </span>
    </div>
  )
}

export default AssignmentCreationForm

